import type { StoryData, StoryToken } from '@/content/stories'
import type {
  ReaderBlock,
  ReaderContent,
  ReaderGlossaryEntry,
  ReaderGrammarSummaryItem,
  ReaderToken,
  ReaderTokenKind
} from '@/types/reader'
import { READER_PROCESSOR_VERSION } from './constants'

const PUNCTUATION_RE =
  /^[\s\u3000-\u303f\uff00-\uffef，。！？、；：,.!?;:"'“”‘’（）()[\]{}《》〈〉「」『』—…·\-]+$/

function storyTokenKind(token: StoryToken): ReaderTokenKind {
  if (token.id) return 'word'
  if (/^[A-Za-z0-9\s]+$/.test(token.s)) return 'latin'
  if (PUNCTUATION_RE.test(token.s)) return 'punctuation'
  return 'unknown'
}

function storyTokenToReaderToken(token: StoryToken): ReaderToken {
  return {
    surface: token.s,
    simplified: token.s,
    traditional: token.t,
    pinyin: token.p,
    zhuyin: token.z,
    entryId: token.id ?? undefined,
    compositeIds: token.compositeIds,
    kind: storyTokenKind(token)
  }
}

function storyGlossaryToReaderGlossary(
  story: StoryData
): Record<string, ReaderGlossaryEntry> {
  const glossary: Record<string, ReaderGlossaryEntry> = {}

  for (const [id, entry] of Object.entries(story.glossary ?? {})) {
    const numericId = Number(id)
    if (!Number.isFinite(numericId)) continue

    glossary[id] = {
      id: numericId,
      simplified: entry.simplified,
      traditional: entry.traditional,
      pinyin: entry.pinyin,
      zhuyin: entry.zhuyin,
      definitions: entry.definitions ?? [],
      hsk: null,
      tocfl: null,
      frequency: null
    }
  }

  return glossary
}

export function storyToReaderContent(story: StoryData): ReaderContent {
  const blocks: ReaderBlock[] = [
    {
      id: 'story-title',
      type: 'section',
      originalText: story.title.s,
      translation: story.title_en,
      tokens: [
        {
          surface: story.title.s,
          simplified: story.title.s,
          traditional: story.title.t,
          kind: 'unknown'
        }
      ]
    }
  ]
  const grammarSummary = new Map<string, ReaderGrammarSummaryItem>()
  let sentenceIndex = 0

  story.paragraphs.forEach((paragraph) => {
    paragraph.sentences.forEach((sentence) => {
      const tokens = sentence.tokens.map(storyTokenToReaderToken)
      sentence.grammarUsages?.forEach((usage) => {
        if (!usage.display) return
        const existing = grammarSummary.get(usage.conceptId)
        if (existing) {
          existing.count += 1
          return
        }
        grammarSummary.set(usage.conceptId, {
          conceptId: usage.conceptId,
          count: 1,
          firstSentenceId: sentence.id,
          ...usage.display
        })
      })
      blocks.push({
        id: `sentence-${sentenceIndex + 1}`,
        type: 'sentence',
        originalText: tokens.map((token) => token.surface).join(''),
        translation: sentence.translation,
        grammar: sentence.grammar ? [sentence.grammar] : undefined,
        grammarUsages: sentence.grammarUsages,
        sourceSentenceId: sentence.id,
        tokens
      })
      sentenceIndex += 1
    })
  })

  return {
    id: `story:${story.slug}`,
    sourceType: 'story',
    title: story.title.s,
    subtitle: story.title_en,
    metadata: {
      slug: story.slug,
      level: story.level,
      topics: story.topics,
      titleTranslation: story.title_en,
      wordCount: story.wordCount,
      createdAt: story.createdAt,
      updatedAt: story.updatedAt,
      isPremiumChapter: story.isPremiumChapter,
      chapterNumber: story.chapterNumber,
      chapterCount: story.chapterCount
    },
    blocks,
    glossary: storyGlossaryToReaderGlossary(story),
    grammarSummary: Array.from(grammarSummary.values()),
    processorVersion: READER_PROCESSOR_VERSION
  }
}
